/**
 * Mobile side menu
 * Contains profile switcher and items found in desktop more menu
 */
import type { IconName } from '@fortawesome/fontawesome-common-types';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import MenuItem from '@components/MenuItem';
import ProfileBubble from '@components/ProfileBubble';

import * as ROUTES from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import type { Profile } from '@models/session';

import { formatName } from '@utils/format';

import {
  DownArrow,
  MenuBottom,
  MenuLinks,
  MobileMenuStyle,
  Note,
  PROFILE_CARD_HEIGHT,
  PROFILE_CARD_WITH_LABEL_HEIGHT,
  ProfileAvatar,
  ProfileEntry,
  ProfileSelector,
  ProfileText,
} from './MobileSideMenu.style';

const messages = defineMessages({
  viewingRecords: {
    id: 'mobileSideMenu.viewingRecords',
    defaultMessage: 'Viewing records for',
  },
  manageAccount: {
    id: 'mobileSideMenu.manageAccount',
    defaultMessage: 'User Profile',
  },
  settings: {
    id: 'mobileSideMenu.settings',
    defaultMessage: 'Settings',
  },
  logOut: {
    id: 'mobileSideMenu.logOut',
    defaultMessage: 'Log Out',
  },
  finishRegistration: {
    id: 'mobileSideMenu.finishRegistration',
    defaultMessage: 'Finish Registration',
  },
  registrationIncomplete: {
    id: 'mobileSideMenu.registrationIncomplete',
    defaultMessage: 'Registration Incomplete',
  },
  appInfo: {
    id: 'mobileSideMenu.appInfo',
    defaultMessage: 'App Information',
  },
});

export interface IMenuLink {
  iconName: IconName;
  route: string;
  label: string;
  show?: boolean;
}

interface MobileSideMenuProps {
  isOpen: boolean;
  onClose?: (closed: boolean) => void;
  links?: IMenuLink[];
}

function MobileSideMenu({
  isOpen = true,
  onClose,
  links = [],
}: MobileSideMenuProps) {
  const { formatMessage } = useIntl();
  const {
    palette: { secondary },
  } = useTheme();
  const {
    state: { activeProfile, allProfiles, loggedInProfile },
    logout,
  } = useSession();
  const { viewAsPatient, reset: resetCuration } = useCuration();

  const [first_name] = [
    activeProfile?.first_name,
    activeProfile?.last_name,
    activeProfile?.profile_picture,
  ];

  const mobileDrawer = useRef<HTMLInputElement>(null);
  const [topOpen, setTopOpen] = useState(false);

  // Logout and clear caches
  async function logOut() {
    await resetCuration();
    logout();
  }

  // Attach mousedown event
  useEffect(() => {
    // Handle click events
    function handleClick(event: MouseEvent) {
      if (
        onClose &&
        mobileDrawer.current &&
        !mobileDrawer.current.contains(event.target as Node)
      ) {
        onClose(false);
      }
    }

    document.addEventListener('mousedown', handleClick, false);
    return () => document.removeEventListener('mousedown', handleClick, false);
  }, [onClose]);

  const handleUserChange = (user: Profile) => {
    viewAsPatient(user.user_id);
    setTopOpen(false);
    if (!user.vitall_patient_id) {
      navigate(ROUTES.DASHBOARD);
    }
  };

  const finished: boolean = true;
  const isViewing = loggedInProfile?.user_id !== activeProfile?.user_id;
  // const isPatient = Boolean(activeProfile?.vitall_patient_id);
  return (
    <MobileMenuStyle
      closed={!isOpen}
      data-testid="mobile-sidemenu"
      ref={mobileDrawer}
    >
      {first_name ? (
        <ProfileSelector
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          firstProfile={finished ? 80 : 120}
          numEntries={allProfiles.length}
          open={topOpen}
        >
          <ProfileEntry
            onClick={() => setTopOpen((t) => !t)}
            style={{
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              height: finished
                ? PROFILE_CARD_HEIGHT
                : PROFILE_CARD_WITH_LABEL_HEIGHT,
            }}
          >
            <ProfileAvatar>
              <ProfileBubble
                s3Key={activeProfile?.profile_picture}
                viewing={activeProfile?.user_id !== loggedInProfile?.user_id}
              />
            </ProfileAvatar>
            <ProfileText active>
              {isViewing && (
                <p>{`${formatMessage(messages.viewingRecords)}:`}</p>
              )}
              {formatName(
                activeProfile?.title,
                activeProfile?.first_name,
                activeProfile?.last_name
              )}
            </ProfileText>
            {allProfiles.length > 1 && (
              <DownArrow>
                <Icon
                  clickable
                  color="text.primary"
                  name="chevron-down"
                  rotation={topOpen ? 180 : 0}
                />
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {!finished && <Note />}
              </DownArrow>
            )}
            {/* {!finished && (
              <FinishLabel onClick={handleNavigate}>
                {formatMessage(messages.finishRegistration)}
              </FinishLabel>
            )} */}
          </ProfileEntry>

          {allProfiles
            .filter(({ user_id }) => user_id !== activeProfile?.user_id)
            .map((user) => (
              <ProfileEntry
                key={user.user_id}
                onClick={() => handleUserChange(user)}
              >
                <ProfileAvatar>
                  <ProfileBubble s3Key={user.profile_picture} />
                </ProfileAvatar>
                <div>
                  <ProfileText active={false}>
                    {formatName(user.title, user.first_name, user.last_name)}
                  </ProfileText>
                  {/* {onboardingSteps[user_id] !== 'done' && (
                    <Incomplete>
                      {formatMessage(messages.registrationIncomplete)}
                    </Incomplete>
                  )} */}
                </div>
              </ProfileEntry>
            ))}
        </ProfileSelector>
      ) : null}

      <MenuLinks finishLabel={!finished}>
        {links.map(({ iconName, label, route, show = true }, index) => {
          if (!show) {
            return null;
          }
          return (
            <MenuItem
              key={`menu-item-${index}`}
              icon={[{ iconColor: secondary.main, iconName: iconName }]}
              onClick={() => {
                navigate(route);
              }}
              text={label}
            />
          );
        })}
      </MenuLinks>

      <MenuBottom>
        <MenuItem
          hideArrow
          icon={[{ iconName: 'circle-user' }]}
          onClick={() => {
            navigate(ROUTES.MANAGE_ACCOUNT);
          }}
          text={formatMessage(messages.manageAccount)}
        />
        <MenuItem
          hideArrow
          icon={[{ iconName: 'circle-info' }]}
          onClick={() => {
            navigate(ROUTES.APP_INFORMATION);
          }}
          text={formatMessage(messages.appInfo)}
        />
        <MenuItem
          hideArrow
          icon={[{ iconName: 'right-from-bracket' }]}
          onClick={logOut}
          text={formatMessage(messages.logOut)}
        />
      </MenuBottom>
    </MobileMenuStyle>
  );
}

export default MobileSideMenu;
